import React, { useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { User, ClipboardCheck, PhoneCall, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function AdmissionForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    studentName: '',
    gender: '',
    dob: '',
    age: '',
    classApplyingFor: '',
    previousSchool: '',
    aadhaar: '',
    address: '',
    fatherName: '',
    motherName: '',
    parentGuardianName: '',
    occupation: '',
    mobileNumber: '',
    whatsappNumber: '',
    email: '',
    referralSource: '',
    additionalNotes: '',
    transportationRequired: 'No'
  });

  const [loading, setLoading] = useState(false);
  const [appNumber, setAppNumber] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const calculateAge = (dobString) => {
    if (!dobString) return '';
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDobChange = (e) => {
    const dobVal = e.target.value;
    const computedAge = calculateAge(dobVal);
    setForm({ ...form, dob: dobVal, age: computedAge });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    const submissionData = {
      ...form,
      parentGuardianName: form.parentGuardianName || form.fatherName || form.motherName
    };

    axios.post(`${API_BASE_URL}/SubmitApplication`, submissionData)
      .then(res => {
        setLoading(false);
        if (res.data.success) {
          setAppNumber(res.data.data.applicationNumber);
          setForm({
            studentName: '',
            gender: '',
            dob: '',
            age: '',
            classApplyingFor: '',
            previousSchool: '',
            aadhaar: '',
            address: '',
            fatherName: '',
            motherName: '',
            parentGuardianName: '',
            occupation: '',
            mobileNumber: '',
            whatsappNumber: '',
            email: '',
            referralSource: '',
            additionalNotes: '',
            transportationRequired: 'No'
          });
        }
      })
      .catch(err => {
        setLoading(false);
        console.error(err);
        alert('There was an issue submitting your application. Please check all fields and try again.');
      });
  };

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-admissions)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative', overflow: 'hidden' }}>
      
      {/* Playful background floating doodles (aligned strictly on left/right margins) */}
      {[
        // Left Side Margin Doodles (Pushed to far left, 1% - 5%)
        { icon: '☁️', top: '2%', left: '2%', size: '2.5rem', opacity: 0.15, class: 'cloud-float' },
        { icon: '✏️', top: '8%', left: '3%', size: '2rem', opacity: 0.18, class: 'float-slow' },
        { icon: '✈️', top: '15%', left: '2%', size: '2rem', opacity: 0.2, class: 'plane-float' },
        { icon: '✨', top: '20%', left: '4%', size: '2rem', opacity: 0.15, class: 'cloud-float' },
        { icon: '🪁', top: '27%', left: '3%', size: '2.8rem', opacity: 0.15, class: 'plane-float' },
        { icon: '🌈', top: '34%', left: '2%', size: '2.5rem', opacity: 0.15, class: 'cloud-float' },
        { icon: '⚽', top: '40%', left: '3%', size: '2.3rem', opacity: 0.12, class: 'sun-spin' },
        { icon: '📐', top: '47%', left: '4%', size: '2.1rem', opacity: 0.14, class: 'plane-float' },
        { icon: '🎒', top: '53%', left: '2%', size: '2.5rem', opacity: 0.16, class: 'cloud-float' },
        { icon: '🔔', top: '60%', left: '3%', size: '2.2rem', opacity: 0.15, class: 'float-slow' },
        { icon: '🍎', top: '67%', left: '2%', size: '2.3rem', opacity: 0.15, class: 'float-slow' },
        { icon: '🧁', top: '74%', left: '3%', size: '2.2rem', opacity: 0.12, class: 'float-slow' },
        { icon: '🦕', top: '80%', left: '2%', size: '2.6rem', opacity: 0.12, class: 'plane-float' },
        { icon: '🎨', top: '86%', left: '3%', size: '2rem', opacity: 0.12, class: 'float-slow' },
        { icon: '🍉', top: '91%', left: '2%', size: '2.3rem', opacity: 0.15, class: 'float-slow' },
        { icon: '🚲', top: '95%', left: '3%', size: '2.2rem', opacity: 0.13, class: 'cloud-float' },
        { icon: '🎈', top: '24%', left: '5%', size: '2.4rem', opacity: 0.13, class: 'cloud-float' },
        { icon: '✏️', top: '64%', left: '4%', size: '2rem', opacity: 0.16, class: 'float-slow' },

        // Right Side Margin Doodles (Pushed to far right, 1% - 5%)
        { icon: '☀️', top: '3%', right: '2%', size: '3rem', opacity: 0.15, class: 'sun-spin' },
        { icon: '🎈', top: '9%', right: '3%', size: '2.5rem', opacity: 0.12, class: 'cloud-float' },
        { icon: '🎨', top: '16%', right: '2%', size: '2.5rem', opacity: 0.12, class: 'float-slow' },
        { icon: '🏫', top: '22%', right: '3%', size: '2.2rem', opacity: 0.14, class: 'float-slow' },
        { icon: '📚', top: '29%', right: '2%', size: '2rem', opacity: 0.12, class: 'float-slow' },
        { icon: '🧸', top: '35%', right: '3%', size: '2.2rem', opacity: 0.15, class: 'float-slow' },
        { icon: '🧪', top: '42%', right: '2%', size: '2rem', opacity: 0.15, class: 'float-slow' },
        { icon: '🪐', top: '48%', right: '3%', size: '2.4rem', opacity: 0.12, class: 'sun-spin' },
        { icon: '🖍️', top: '55%', right: '2%', size: '2rem', opacity: 0.15, class: 'cloud-float' },
        { icon: '🎸', top: '61%', right: '3%', size: '2.5rem', opacity: 0.14, class: 'plane-float' },
        { icon: '🎓', top: '67%', right: '2%', size: '2.3rem', opacity: 0.15, class: 'float-slow' },
        { icon: '📝', top: '74%', right: '3%', size: '2rem', opacity: 0.12, class: 'cloud-float' },
        { icon: '🚀', top: '80%', right: '2%', size: '2.4rem', opacity: 0.14, class: 'cloud-float' },
        { icon: '🧩', top: '86%', right: '3%', size: '2.1rem', opacity: 0.13, class: 'sun-spin' },
        { icon: '🚌', top: '91%', right: '2%', size: '2.5rem', opacity: 0.12, class: 'plane-float' },
        { icon: '🏆', top: '95%', right: '3%', size: '2.4rem', opacity: 0.14, class: 'plane-float' },
        { icon: '☁️', top: '12%', right: '4%', size: '2.2rem', opacity: 0.15, class: 'cloud-float' },
        { icon: '🖍️', top: '71%', right: '4%', size: '2rem', opacity: 0.14, class: 'cloud-float' }
      ].map((doodle, idx) => (
        <div 
          key={idx}
          className={doodle.class}
          style={{
            position: 'absolute',
            top: doodle.top,
            left: doodle.left || 'auto',
            right: doodle.right || 'auto',
            fontSize: doodle.size,
            opacity: doodle.opacity,
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          {doodle.icon}
        </div>
      ))}

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 10 }}>
        
        <AnimatePresence mode="wait">
          {!appNumber ? (
            <motion.div 
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: '#ffffff', padding: '40px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)', borderTop: '8px solid var(--accent-coral)' }}
            >
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <ClipboardCheck size={48} style={{ color: 'var(--accent-coral)', marginBottom: '12px' }} />
                <h1 style={{ fontSize: '2.25rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Student Admission Form</h1>
                <p style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>Fill in all the details below. We will review details and confirm seat reservation.</p>
                
                {/* Download option block */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                  <a 
                    href="https://docs.google.com/document/d/1Xy_J2xJq_d7R7vO2S8fX1b2Y_Z44b2mN/export?format=pdf" 
                    download 
                    target="_blank" 
                    rel="noreferrer"
                    className="btn-premium btn-premium-secondary"
                    style={{ height: '42px', fontSize: '0.88rem', gap: '8px', borderRadius: '12px', boxShadow: 'var(--shadow-sm)' }}
                  >
                    <Download size={16} /> Download Physical PDF Form
                  </a>
                </div>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                
                {/* Student Info */}
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                  <h3 style={sectionHeaderStyle}><User size={18} /> Student Information</h3>
                  <div style={gridStyle}>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={labelStyle}>Student Full Name *</label>
                      <input type="text" name="studentName" value={form.studentName} onChange={handleChange} className="admission-custom-input" placeholder="Enter student's full name" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Gender *</label>
                      <select name="gender" value={form.gender} onChange={handleChange} className="admission-custom-input" required>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Date of Birth *</label>
                      <input type="date" name="dob" value={form.dob} onChange={handleDobChange} className="admission-custom-input" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Computed Age *</label>
                      <input type="number" name="age" value={form.age} onChange={handleChange} className="admission-custom-input" placeholder="Age" min="2" max="18" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Class Applying For *</label>
                      <select name="classApplyingFor" value={form.classApplyingFor} onChange={handleChange} className="admission-custom-input" required>
                        <option value="">Select Class</option>
                        <option value="Pre-Nursery">Pre-Nursery</option>
                        <option value="Nursery">Nursery</option>
                        <option value="KG-I">KG-I</option>
                        <option value="KG-II">KG-II</option>
                        <option value="Class I">Class I</option>
                        <option value="Class II">Class II</option>
                        <option value="Class III">Class III</option>
                        <option value="Class IV">Class IV</option>
                        <option value="Class V">Class V</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={labelStyle}>Previous School Attended (If any)</label>
                      <input type="text" name="previousSchool" value={form.previousSchool} onChange={handleChange} className="admission-custom-input" placeholder="Enter previous school details" />
                    </div>
                    <div>
                      <label style={labelStyle}>Aadhaar Card Number (Optional)</label>
                      <input type="text" name="aadhaar" value={form.aadhaar} onChange={handleChange} className="admission-custom-input" placeholder="12-digit number" />
                    </div>
                  </div>
                </motion.div>

                {/* Parent Info */}
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
                  <h3 style={sectionHeaderStyle}><PhoneCall size={18} /> Parent & Guardian Information</h3>
                  <div style={gridStyle}>
                    <div>
                      <label style={labelStyle}>Father's Name *</label>
                      <input type="text" name="fatherName" value={form.fatherName} onChange={handleChange} className="admission-custom-input" placeholder="Father's Name" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Mother's Name *</label>
                      <input type="text" name="motherName" value={form.motherName} onChange={handleChange} className="admission-custom-input" placeholder="Mother's Name" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Primary Contact Guardian Name</label>
                      <input type="text" name="parentGuardianName" value={form.parentGuardianName} onChange={handleChange} className="admission-custom-input" placeholder="Guardian's Name (Optional)" />
                    </div>
                    <div>
                      <label style={labelStyle}>Occupation / Business</label>
                      <input type="text" name="occupation" value={form.occupation} onChange={handleChange} className="admission-custom-input" placeholder="Occupation" />
                    </div>
                    <div>
                      <label style={labelStyle}>Mobile Phone Number *</label>
                      <input type="tel" name="mobileNumber" value={form.mobileNumber} onChange={handleChange} className="admission-custom-input" placeholder="10-digit number" required />
                    </div>
                    <div>
                      <label style={labelStyle}>WhatsApp Phone Number *</label>
                      <input type="tel" name="whatsappNumber" value={form.whatsappNumber} onChange={handleChange} className="admission-custom-input" placeholder="WhatsApp number" required />
                    </div>
                    <div style={{ gridColumn: 'span 3' }}>
                      <label style={labelStyle}>Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} className="admission-custom-input" placeholder="parent@example.com" required />
                    </div>
                    <div style={{ gridColumn: 'span 3' }}>
                      <label style={labelStyle}>Residential Address *</label>
                      <textarea name="address" value={form.address} onChange={handleChange} className="admission-custom-input" rows="3" placeholder="Enter full residential address" required />
                    </div>
                  </div>
                </motion.div>

                {/* Additional Info */}
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
                  <h3 style={sectionHeaderStyle}><AlertCircle size={18} /> Referral & Additional Remarks</h3>
                  <div style={gridStyle}>
                    <div>
                      <label style={labelStyle}>How did you hear about us? *</label>
                      <select name="referralSource" value={form.referralSource} onChange={handleChange} className="admission-custom-input" required>
                        <option value="">Select Option</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Newspaper / Banner">Newspaper / Banner</option>
                        <option value="Friends & Family">Friends & Family</option>
                        <option value="School Alumni">School Alumni</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Transport Required?</label>
                      <select name="transportationRequired" value={form.transportationRequired} onChange={handleChange} className="admission-custom-input">
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 3' }}>
                      <label style={labelStyle}>Remarks / Notes / Special requests</label>
                      <textarea name="additionalNotes" value={form.additionalNotes} onChange={handleChange} className="admission-custom-input" rows="3" placeholder="Remarks or references..." />
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.3 }}>
                  <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '52px', fontSize: '1.05rem', marginTop: '16px', width: '100%' }} disabled={loading}>
                    {loading ? 'Registering details...' : 'Submit Enrollment Request'}
                  </button>
                </motion.div>

              </form>
            </motion.div>
          ) : (
            <motion.div 
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              style={{ backgroundColor: '#ffffff', padding: '48px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-xl)', textAlign: 'center' }}
            >
              <CheckCircle2 size={64} style={{ color: 'var(--success-color)', marginBottom: '24px', margin: '0 auto' }} />
              <h2 style={{ fontSize: '2.25rem', color: 'var(--text-primary)', marginTop: '16px', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Application Registered!</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', fontWeight: 700 }}>Your school admission application has been registered successfully in our database.</p>
              
              {/* Receipt card */}
              <div style={{ backgroundColor: 'var(--bg-admissions)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-color)', maxWidth: '400px', margin: '0 auto 32px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 700 }}>UNIQUE APPLICATION NUMBER</p>
                <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary-dark)', fontFamily: 'monospace' }}>{appNumber}</h3>
              </div>

              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button onClick={() => navigate('/')} className="btn-premium btn-premium-secondary">
                  Back to Home
                </button>
                <button onClick={() => setAppNumber(null)} className="btn-premium btn-premium-primary">
                  Apply for Another Student
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}

// Styling Constants
const sectionHeaderStyle = {
  fontSize: '1.25rem',
  color: 'var(--text-primary)',
  marginBottom: '20px',
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderBottom: '2px dashed var(--border-color)',
  paddingBottom: '8px',
  fontFamily: 'Poppins'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '20px'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  marginBottom: '6px'
};

// Add standard styles
const styleElement = document.createElement('style');
styleElement.innerHTML = `
  .admission-custom-input {
    width: 100%;
    padding: 12px 16px;
    font-size: 0.95rem;
    font-weight: 700;
    font-family: 'Quicksand', sans-serif;
    border: 2px solid var(--border-color);
    border-radius: var(--border-radius-form);
    background-color: #f8fafc;
    color: var(--text-primary);
    outline: none;
    transition: all 0.25s ease;
  }
  .admission-custom-input:focus {
    border-color: var(--primary-color) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.15) !important;
  }
`;
document.head.appendChild(styleElement);
